import _ from 'lodash';
import { cgiDimensions } from '../../common/cgi/Cgi';
import { addTransparency } from '../../common/cgi/cgiHelpers';

const getTokens = (id) => {
  const tokens = _.split(id, /_|-/);
  const uniqueTokens = _.uniq(tokens);
  return _.compact(uniqueTokens);
};

const buildMmcPegData = (style) => {
  const { cgiCode, alternateId } = style;

  if (_.endsWith(cgiCode, '-DRW')) {
    const tokens = cgiCode.replace('-DRW', '').split('__');
    return {
      mmc: `${tokens[0]}D`,
      peg: `${tokens[0]}D__${tokens[1]}`,
    };
  }

  const tokens = getTokens(alternateId || cgiCode);
  return {
    mmc: tokens[0],
    peg: `${tokens[0]}__${tokens[1]}`,
  };
};

const sortWithDefaultFirst = (angles) => {
  const defaultAngles = angles.filter((angle) => angle.defaultAngle);
  const nonDefaultAngles = angles.filter((angle) => !angle.defaultAngle);
  return defaultAngles.concat(nonDefaultAngles);
};

const createAngleMap = (options, url) => (angle) => {
  const { appResources, breakpoint, isConvertibleTopDown } = options;
  const { cgiSize } = appResources;

  const buildUrl = _.template(url);
  const context = {
    degree: angle.value,
    removeCat: isConvertibleTopDown ? 'CONV' : '',
  };
  const isSmall = breakpoint === 'small' || breakpoint === 'xsmall';

  const urlContext = _.assign({}, context, {
    imageSize: isSmall ? cgiSize.small : cgiSize.large,
  });
  const zoomUrlContext = _.assign({}, context, {
    imageSize: isSmall ? cgiSize.zoomSmall : cgiSize.xxLarge,
  });

  return {
    id: angle.value,
    profile: angle.profileView,
    zoom: angle.displayZoom,
    url: buildUrl(urlContext),
    zoomUrl: buildUrl(zoomUrlContext),
    cgiZoomDimensions: isSmall
      ? cgiDimensions[cgiSize.large]
      : cgiDimensions[cgiSize.xxLarge],
  };
};

export const normalize = (
  appResources,
  config,
  isConvertibleTopDown,
  breakpoint,
  year
) => {
  const { selections, style } = config;
  const { rpos, interiorColor, exteriorColor } = selections;
  const { mmc, peg } = style;
  const { interiorAngles, exteriorAngles, year: defaultYear } = appResources;

  const options = {
    appResources,
    mmc,
    peg,
    rpos,
    interiorColor,
    exteriorColor,
    isConvertibleTopDown,
    breakpoint,
    year: year || defaultYear,
  };

  const interior = sortWithDefaultFirst(interiorAngles).map(
    createAngleMap(options, config.interior.cgiImageUrl)
  );
  const exterior = sortWithDefaultFirst(exteriorAngles).map(
    createAngleMap(options, config.exterior.cgiImageUrl)
  );

  return {
    interior,
    exterior,
  };
};

const normalizeCGI = (modelInfo, style, angle, size) => {
  const { year, cgiData } = modelInfo;

  const buildUrl = _.template(cgiData.cgiUrlTemplate);

  const { mmc, peg } = buildMmcPegData(style);

  const context = {
    renderStandards: true,
    removeCat: '',
    colorRPO: style.defaultColor,
    imageSize: size,
    optionRPOs: '',
    interior: '',
    mmc,
    peg,
    degree: angle,
    year,
  };

  return buildUrl(context);
};

export const normalizeCGIForCards = (modelInfo, style, angle, size) => {
  return `${normalizeCGI(modelInfo, style, angle, size)}&background=ffffff`;
};

export const normalizeCGIForCompare = (modelInfo, style, angle, size) => {
  return addTransparency(normalizeCGI(modelInfo, style, angle, size));
};

const buildCgiUrl = (url, cgiSizes, background) =>
  _.template(url)({ imageSize: cgiSizes['small'], background: background });

export const buildCgiImageForCard = (url, cgiSizes) => {
  return buildCgiUrl(url, cgiSizes, 'ffffff');
};

export const buildCgiImageForCompare = (url, cgiSizes) => {
  return addTransparency(buildCgiUrl(url, cgiSizes, ''));
};
